/**
 * Camada 1 · registro e carregamento de ProductConfig.
 *
 * POR QUE VALIDAR EM TEMPO DE EXECUCAO SE JA EXISTE O TIPO: o TypeScript garante a forma
 * de quem compila junto, e nao garante nada do que chega de um arquivo carregado
 * dinamicamente nem de uma configuracao vinda do backend. Sem esta validacao, um produto
 * com `menu: []` sobe e mostra um rail vazio, e o defeito so aparece na tela do cliente.
 */

import { ProductConfigInvalida, type ProductConfig } from './types.js';

/** Registro em memoria. Chave e o id do produto. */
const registro = new Map<string, ProductConfig>();

/**
 * Os valores aceitos em `mensagens`, e as duas listas sao FECHADAS e POSITIVAS.
 *
 * POR QUE POSITIVA E NAO NEGATIVA: escrever `lado !== 'cliente'` da o mesmo resultado hoje e
 * deixa de dar no dia em que um terceiro valor nascer, porque ele entraria pela porta sem
 * ninguem decidir. A lista diz quem PODE, entao valor novo so passa depois de alguem
 * acrescenta-lo aqui de proposito.
 *
 * TIPADAS COMO `readonly string[]` DE PROPOSITO: o tipo da uniao ja e checado por quem
 * compila junto, e nao alcanca manifesto carregado dinamicamente nem configuracao vinda do
 * backend, que e exatamente o que este arquivo existe para pegar. Tipar pela uniao faria a
 * comparacao virar tautologia para o compilador e a checagem deixaria de valer para o unico
 * caso em que ela importa.
 */
const LADOS_DE_MENSAGENS: readonly string[] = ['cliente', 'operacao'];
const PLANOS_DE_MENSAGENS: readonly string[] = ['tenant', 'control-plane'];

/**
 * Confere o que o tipo nao alcanca: valor vazio, formato de id e coerencia entre campos.
 * Devolve a lista de problemas, vazia quando esta tudo certo.
 */
export function validarProductConfig(config: ProductConfig): string[] {
  const problemas: string[] = [];

  // O id entra em rota, em chave de armazenamento e em registro de auditoria. Espaco e
  // maiuscula ali produzem URL que quebra e chave que duplica.
  if (!/^[a-z0-9-]+$/.test(config.id)) {
    problemas.push('id deve ter apenas minusculas, numeros e hifen');
  }

  if (config.nome.trim() === '') problemas.push('nome nao pode ser vazio');

  // Duas letras e o limite do avatar quadrado do rail. Tres nao cabem sem diminuir a
  // fonte a ponto de ninguem ler.
  if (config.iniciais.length < 1 || config.iniciais.length > 2) {
    problemas.push('iniciais devem ter 1 ou 2 caracteres');
  }

  // Produto sem menu sobe com o rail vazio e parece quebrado. Melhor recusar na partida.
  if (config.menu.length === 0) problemas.push('menu nao pode ser vazio');

  // Rota duplicada faz dois itens levarem ao mesmo lugar, e um deles fica morto para sempre.
  const rotas = config.menu.map((i) => i.rota);
  const repetidas = rotas.filter((r, i) => rotas.indexOf(r) !== i);
  if (repetidas.length > 0) {
    problemas.push(`rotas repetidas no menu: ${[...new Set(repetidas)].join(', ')}`);
  }

  // Rota sem barra inicial vira caminho relativo e resolve diferente conforme a tela atual.
  const semBarra = config.menu.filter((i) => !i.rota.startsWith('/'));
  if (semBarra.length > 0) {
    problemas.push(`rotas devem comecar com barra: ${semBarra.map((i) => i.rota).join(', ')}`);
  }

  // UM DESTAQUE, NO MAXIMO. Marcar dois anula o destaque, que e o ponto inteiro dele: a
  // tela passa a ter dois itens realcados e nenhum se sobressai. O tipo `boolean` deixaria
  // passar, e por isso a checagem e aqui.
  const destacados = config.menu.filter((i) => i.destaque);
  if (destacados.length > 1) {
    problemas.push(
      `no maximo um item do menu pode ter destaque, e ha ${destacados.length}: ${destacados.map((i) => i.rota).join(', ')}`,
    );
  }

  // QUATRO NA BARRA INFERIOR, NO MAXIMO, e o numero nao e arbitrario: com cinco, cada alvo
  // cai abaixo dos 44px em 390px de largura, e a barra vira uma fileira de botoes que
  // ninguem acerta. Recusar aqui e melhor que descobrir na tela do cliente.
  const naBarra = config.menu.filter((i) => i.naBarraInferior);
  if (naBarra.length > 4) {
    problemas.push(
      `a barra inferior do celular cabe 4 itens sem apertar o alvo de toque, e ha ${naBarra.length}: ${naBarra.map((i) => i.rota).join(', ')}`,
    );
  }

  // A rota da marca NAO precisa estar no menu, e e justamente esse o caso de uso (a tela
  // "Sobre o sistema" do prontidao). O que ela precisa e ser uma rota de verdade: sem a
  // barra inicial vira caminho relativo e resolve diferente conforme a tela atual.
  if (config.rotaDaMarca !== undefined && !config.rotaDaMarca.startsWith('/')) {
    problemas.push(`rotaDaMarca deve comecar com barra: ${config.rotaDaMarca}`);
  }

  // Aviso de privacidade DECLARADO E VAZIO e pior que aviso ausente: ausente nao desenha
  // link nenhum, e vazio desenha um link que abre uma tela em branco. Quem clica num link
  // de privacidade e exatamente quem esta desconfiado, e a tela vazia confirma a desconfianca.
  if (config.login.privacidade !== undefined) {
    // A VERSAO DECLARADA E VAZIA e pior que ausente: ausente nao compila, e vazia compila
    // e vira uma string vazia no banco, gravada como "a versao aceita". Aceite com versao
    // vazia nao prova nada, e o defeito so apareceria numa disputa.
    if (config.login.privacidade.versao.trim() === '') {
      problemas.push(
        'login.privacidade.versao nao pode ser vazio: e ela que diz QUAL texto a pessoa aceitou, e sem ela o registro de aceite nao prova nada',
      );
    }
    if (config.login.privacidade.secoes.length === 0) {
      problemas.push('login.privacidade.secoes nao pode ser vazio: link que abre tela em branco e pior que link nenhum');
    }
    const semTexto = config.login.privacidade.secoes.filter((sec) => sec.paragrafos.length === 0);
    if (semTexto.length > 0) {
      problemas.push(
        `secoes de privacidade sem paragrafo: ${semTexto.map((sec) => sec.titulo).join(', ')}`,
      );
    }
  }

  if (config.ajuda.namespace.trim() === '') {
    problemas.push('ajuda.namespace nao pode ser vazio: sem ele o painel de ajuda mostra a base inteira');
  }

  // A CONVERSA E O SINO. A ausencia nao e problema AQUI, e so enquanto o campo for opcional:
  // quem a torna problema e o passo 8 da ordem de implantacao, junto com a retirada do `?`.
  // O que ja se recusa e o valor ERRADO, que e o caso perigoso: `plano: 'controlplane'` sem
  // hifen compila igual, e o sino do admin passaria a falar com o servico do tenant, tomaria
  // 401 e ficaria indisponivel sem ninguem entender por que.
  /**
   * A PRESENCA E EXIGIDA DESDE 06/09/2026, junto com a saida do `?` no tipo.
   *
   * O TYPECHECK JA RECUSA quem nao declarar, e mesmo assim esta checagem existe: manifesto
   * tambem chega de JSON, de banco e de arquivo escrito a mao, e nada disso passa pelo
   * compilador. A tranca de tipo protege quem escreve TypeScript; esta protege o resto.
   *
   * SEM ELA, um manifesto sem `mensagens` cairia no padrao silencioso do chassi, e num app
   * de plano de controle esse padrao esta ERRADO: o sino somaria as proprias respostas do
   * operador como nao lidas.
   */
  if (config.mensagens === undefined) {
    problemas.push(
      'mensagens e obrigatorio: sem ele o app nao diz de que lado esta quem usa nem em que ' +
        'plano a conversa vive, e o chassi nao tem como escolher o transporte',
    );
  } else {
    if (!LADOS_DE_MENSAGENS.includes(config.mensagens.lado)) {
      problemas.push(
        `mensagens.lado invalido: veio "${config.mensagens.lado}", e os aceitos sao ${LADOS_DE_MENSAGENS.join(' e ')}`,
      );
    }
    if (!PLANOS_DE_MENSAGENS.includes(config.mensagens.plano)) {
      problemas.push(
        `mensagens.plano invalido: veio "${config.mensagens.plano}", e os aceitos sao ${PLANOS_DE_MENSAGENS.join(' e ')}`,
      );
    }
  }

  // Cota negativa passaria pelo tipo `number` e viraria um teto impossivel de cumprir.
  if (config.ia.cotaMensalTokens < 0) {
    problemas.push('ia.cotaMensalTokens nao pode ser negativa');
  }

  // IA ligada com cota zero e o caso ambiguo: zero aqui significa "sem teto proprio,
  // usa o do plano", entao e valido. O que nao pode e IA desligada com cota positiva,
  // porque o numero passa a mentir sobre um recurso que nao existe naquele produto.
  if (!config.ia.habilitada && config.ia.cotaMensalTokens > 0) {
    problemas.push('ia.cotaMensalTokens deve ser 0 quando ia.habilitada e false');
  }

  return problemas;
}

/**
 * Registra um produto. Lanca quando a configuracao e invalida, de proposito: produto mal
 * configurado precisa quebrar na partida, e nao desenhar meia tela para o cliente.
 */
export function registrarProduto(config: ProductConfig): void {
  const problemas = validarProductConfig(config);
  if (problemas.length > 0) throw new ProductConfigInvalida(config.id, problemas);
  registro.set(config.id, config);
}

/** Devolve o produto, ou undefined. Quem chama decide o que fazer com a ausencia. */
export function obterProduto(id: string): ProductConfig | undefined {
  return registro.get(id);
}

/** Todos os produtos registrados, na ordem em que entraram. */
export function listarProdutos(): readonly ProductConfig[] {
  return [...registro.values()];
}

/** Esvazia o registro. Existe para o teste conseguir partir de um estado limpo, e nao
 *  deve ser chamado em codigo de producao. */
export function limparRegistro(): void {
  registro.clear();
}
